import { motion } from "framer-motion";
import RichText from "@/components/site/RichText";
import { useSiteContent } from "@/lib/SiteContentProvider";
import SectionGlow from "@/components/site/SectionGlow";

export const WorkHistory = () => {
    const { data } = useSiteContent();
    const wh = data?.work_history || {};
    const collab = (wh.collab_brands || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const featured = (wh.featured || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const current = (wh.current_roles || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const titleLines = wh.title_lines?.length ? wh.title_lines : ["A decade of work for", "{gold:brands that move markets}."];

    return (
        <section
            id="history"
            className="pg-section pg-surface-teal"
            aria-label="Work History and Brand Collaborations"
            data-testid="work-history-section"
        >
            <SectionGlow tone="cool" />
            <div className="pg-container">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-5"
                    >
                        <h2
                            className="pg-h2-section"
                            style={{ color: "var(--pg-fg)" }}
                        >
                            {titleLines.map((line, idx) => (
                                <span key={`wh-${idx}`} className="block">
                                    <RichText value={line} />
                                </span>
                            ))}
                        </h2>
                        <p className="pg-lede mt-5 max-w-md">
                            {wh.description}
                        </p>
                    </motion.div>

                    {/* Brand tiles */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-7"
                    >
                        <div
                            className="flex flex-wrap justify-center gap-2.5 sm:gap-3"
                            data-testid="collab-brand-grid"
                        >
                            {collab.map((b, idx) => (
                                <motion.span
                                    key={b.id || b.name}
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.4, delay: 0.025 * idx, ease: [0.16, 1, 0.3, 1] }}
                                    className="pg-collab-tile text-base sm:text-lg"
                                >
                                    {b.name}
                                </motion.span>
                            ))}
                        </div>

                        {/* Featured history rows */}
                        <div className="mt-8" data-testid="featured-history">
                            {featured.map((row, idx) => (
                                <motion.div
                                    key={row.id || row.brand}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.45, delay: 0.04 * idx }}
                                    className="flex items-center justify-between border-b py-4"
                                    style={{ borderColor: "var(--pg-border)" }}
                                >
                                    <div>
                                        <div className="font-display text-xl sm:text-2xl" style={{ color: "var(--pg-fg)" }}>
                                            {row.brand}
                                        </div>
                                        {row.scope && (
                                            <div
                                                className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em]"
                                                style={{ color: "var(--pg-fg-muted)" }}
                                            >
                                                {row.scope}
                                            </div>
                                        )}
                                    </div>
                                    {row.year && (
                                        <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-accent)" }}>
                                            {row.year}
                                        </span>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Currently leading */}
                {current.length > 0 && (
                    <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="currently-leading">
                        {current.map((row, idx) => (
                            <motion.div
                                key={row.id || row.brand}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: 0.05 * idx }}
                                className="pg-card pg-card-luxury p-6 sm:p-7"
                            >
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-accent)" }}>
                                    Currently Leading · Ongoing
                                </span>
                                <h3 className="font-display mt-2 text-2xl sm:text-3xl" style={{ color: "var(--pg-fg)" }}>
                                    {row.brand}
                                </h3>
                                <p className="mt-3 text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                                    {row.role}
                                </p>
                                {row.sector && (
                                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--pg-fg-muted)" }}>
                                        {row.sector}
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default WorkHistory;
